/**
 * The firm's automations for one case, as the Case Automations board shows
 * them: each runs on this case, in the chat beside it.
 */
import { Plus } from "lucide-react";
import type { FC } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/ui/page-header";
import type { StarterSkill } from "@/features/automations/components/skillModel";
import type { FirmSkillSummary } from "@/features/firmKnowledge/types";

import { automationsPath } from "../routes";
import { AutomationCard } from "./AutomationCard";
import type { ExploreGroup } from "./caseExploreStarters";
import { ExploreStarterCard } from "./ExploreStarterCard";

type Props = {
  /** The firm's own automations this case offers. */
  skills: FirmSkillSummary[];
  /** CaseBlink starters to discover, grouped for Explore. */
  exploreGroups?: ExploreGroup[];
  onRun: (skill: FirmSkillSummary) => void;
  /** Run a CaseBlink starter on this case, without copying it first. */
  onRunStarter: (starter: StarterSkill) => void;
};

export const AutomationsBoard: FC<Props> = ({
  skills,
  exploreGroups = [],
  onRun,
  onRunStarter
}) => {
  const exploreCount = exploreGroups.reduce((n, g) => n + g.starters.length, 0);

  return (
    <div className="flex flex-col gap-6" data-testid="automations-board">
      <section className="flex flex-col gap-2" data-testid="automations-mine">
        <SectionHeader title="My Automations" />
        {skills.length ? (
          <div className="grid gap-2 @lg:grid-cols-2">
            {skills.map((skill) => (
              <AutomationCard key={skill.id} skill={skill} onRun={onRun} />
            ))}
          </div>
        ) : (
          <div
            className="flex flex-col items-start gap-2 rounded-xl bg-fill-1 p-4"
            data-testid="automations-mine-empty"
          >
            <p className="text-base text-fg-2">
              You have no automations yet. Add one to run it on this case.
            </p>
            <Button asChild variant="secondary" size="sm">
              <Link to={automationsPath({ newSkill: true })}>
                <Plus />
                Add Automation
              </Link>
            </Button>
          </div>
        )}
      </section>

      {exploreCount ? (
        <section
          className="flex flex-col gap-4"
          data-testid="automations-explore"
        >
          <SectionHeader title="Explore" />
          {exploreGroups.map((group) => (
            <div
              key={group.title}
              className="flex flex-col gap-2"
              data-testid="automations-explore-group"
            >
              <h3 className="text-base font-medium text-fg">{group.title}</h3>
              <div className="grid gap-2 @lg:grid-cols-2">
                {group.starters.map((starter) => (
                  <ExploreStarterCard
                    key={starter.id}
                    starter={starter}
                    onRun={onRunStarter}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {skills.length ? (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="self-start text-fg-2"
        >
          <Link to={automationsPath({})} data-testid="automations-manage">
            <Plus />
            New Automation
          </Link>
        </Button>
      ) : null}
    </div>
  );
};
